import type { Theme } from "@mui/material";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import z from "zod";

import { DIALOG_WIDTH } from "../../constants";
import type { CloseDialogHandler } from "../../contexts/DialogContext";
import { useCreateServiceTemplate } from "../../hooks/mutation/create/useCreateServiceTemplate";
import { useKpis } from "../../hooks/query/useKpis";
import type { ServiceTemplate } from "../../types";
import StandardDialogActions from "./StandardDialogActions";
import StandardDialogTitle from "./StandardDialogTitle";

const schema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  description: z.string().trim().min(1, "Description is required"),
  type: z.enum(["saas", "retail", "banking", "internal_it"]),
  kpiIds: z.array(z.string()).min(1, "Select at least one KPI"),
});

type FormValues = z.infer<typeof schema>;

const templateTypeOptions: Array<{
  value: ServiceTemplate["type"];
  label: string;
}> = [
  { value: "saas", label: "SaaS" },
  { value: "retail", label: "Retail" },
  { value: "banking", label: "Banking" },
  { value: "internal_it", label: "Internal IT" },
];

const NewServiceTemplateDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
}> = ({ onDialogClosed }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const { data: kpis = [] } = useKpis();
  const { mutateAsync: createTemplate, isPending: isCreating } = useCreateServiceTemplate();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      type: "saas",
      kpiIds: [],
    },
    mode: "onBlur",
  });

  const onSubmit = async (data: FormValues) => {
    try {
      setSubmitError(null);
      await createTemplate({
        name: data.name.trim(),
        description: data.description.trim(),
        type: data.type,
        kpiIds: data.kpiIds,
      });

      reset({ name: "", description: "", type: "saas", kpiIds: [] });
      onDialogClosed({ message: "Service template created!", severity: "success" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const onClose = () => {
    if (isCreating) {
      return;
    }
    setSubmitError(null);
    reset({ name: "", description: "", type: "saas", kpiIds: [] });
    onDialogClosed();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{ sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg } }}
    >
      <StandardDialogTitle
        title="New Service Template"
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <Box component="form" id="new-service-template-form" onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Create a reusable service template and map its default KPIs.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={2.25} sx={{ width: "100%" }}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. API Gateway"
                  error={!!errors.name}
                  {...register("name", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.name}>
                  {errors.name?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Template description"
                  error={!!errors.description}
                  {...register("description", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.description}>
                  {errors.description?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl error={!!errors.type}>
                <FormLabel>Type</FormLabel>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <Select
                      size="small"
                      value={field.value}
                      onChange={(event) => {
                        field.onChange(event.target.value);
                        if (submitError) {
                          setSubmitError(null);
                        }
                      }}
                    >
                      {templateTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors.type?.message ?? " "}</FormHelperText>
              </FormControl>

              <FormControl error={!!errors.kpiIds}>
                <FormLabel>Default KPIs</FormLabel>
                <Controller
                  name="kpiIds"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      filterSelectedOptions
                      options={kpis}
                      value={kpis.filter((kpi) => (field.value ?? []).includes(kpi.id))}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      getOptionLabel={(option) => option.name}
                      onChange={(_, nextOptions) => {
                        field.onChange(nextOptions.map((option) => option.id));
                        if (submitError) {
                          setSubmitError(null);
                        }
                      }}
                      ListboxProps={{
                        sx: { p: 0 },
                      }}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.name}
                            size="small"
                            color="primary"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox checked={selected} />
                          <ListItemText primary={option.name} secondary={option.description} />
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder={(field.value ?? []).length === 0 ? "Select KPIs" : ""}
                          error={!!errors.kpiIds}
                        />
                      )}
                    />
                  )}
                />
                <FormHelperText>{errors.kpiIds?.message ?? " "}</FormHelperText>
              </FormControl>

              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText="Create"
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isCreating}
          disabled={isCreating}
        />
      </Box>
    </Dialog>
  );
};

export default NewServiceTemplateDialog;
